import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CommonService} from '../../../core/service/common.service';
import {SessionService} from '../../../core/service/session.service';
import {
  EventService,
  NyuukaJisshiCondDto,
  NyuukaJisshiReqDto,
  ShukkaJisshiReqDto
} from '../../../core/service/event.service';
import {MasterService} from '../../../core/service/master.service';
import {
  COLLECTION_NAME,
  EVENT_SHUTOKU_DATERANGE_LIMIT,
  IKUSUU,
  KEISHA_SHUBETSU, NYUUKA_SHUKKA_EDITABLE_LIMIT,
  SHIYOULOT_KEY_SUFFIX
} from '../../constants';
import {Subscription} from 'rxjs';
import {exceptKeyWithOptional, JisshiDateNonNullable, yyyyMMdd} from '../../typeAliases';
import {ShukkaEvent} from '../../interfaces/shukka-event';
import {AbstractEventJisshiNaiyou} from '../../interfaces/abstract-event-jisshi-naiyou';
import {NyuukaEvent} from '../../interfaces/nyuuka-event';
import {ChumonForIwamura} from '../../interfaces/chumon';
import {ShiyoulotService} from "../../../core/service/shiyoulot.service";
import {ShiyoulotV2} from "../../interfaces/shiyoulot";
import cloneDeep from 'lodash-es/cloneDeep';
import {MailService} from '../../../core/service/mail.service';
import {WasuuErrorStateMatcher} from '../../../core/error-state/wasuu-error-state-matcher';

@Component({
  selector: 'app-shukka-event-dialog',
  templateUrl: './shukka-event-dialog.component.html',
  styleUrls: ['./shukka-event-dialog.component.scss',
    './../../layout/layout.component.scss']
})
export class ShukkaEventDialogComponent implements OnInit, OnDestroy {
  selectedMeisai: ShukkaEvent;
  inputForm: exceptKeyWithOptional<AbstractEventJisshiNaiyou, 'jisshiDate'> = this.eventService.getInitialAbstractEventJisshiNaiyou();
  editEvent = false;
  subscription: Subscription = new Subscription();
  edukeDate: yyyyMMdd;
  shukkaWasuu?: number;
  // 登録・取り消しの処理中フラグ(両操作を相互排他し、二度押しによる二重実行を防ぐ)
  isProcessing = false;
  shukkaSakiLotInfo?: string; // 出荷先ロット名前 + 鶏舎
  ikusuu = IKUSUU;
  chumonInfo: string | null = null; // 注文番号 + 出荷先
  NYUUKA_SHUKKA_EDITABLE_LIMIT = NYUUKA_SHUKKA_EDITABLE_LIMIT;
  todayString = this.commonService.getDateWithString();
  // 90日前から今日までの範囲指定可能
  registerLimitDate = this.commonService.genOffsetDateWithStandardDateString(
    this.todayString, -NYUUKA_SHUKKA_EDITABLE_LIMIT);

  constructor(@Inject(MAT_DIALOG_DATA) public data: ShukkaEvent[],
              public matDialogRef: MatDialogRef<ShukkaEventDialogComponent>,
              public commonService: CommonService,
              public sessionService: SessionService,
              public eventService: EventService,
              public masterService: MasterService,
              public shiyoulotService: ShiyoulotService,
              private mailService: MailService,
              public wasuuErrorStateMatcher: WasuuErrorStateMatcher,
              ) {
    this.selectedMeisai = this.data[0];
    this.edukeDate = this.selectedMeisai.taishouEdukeDate;
    this.shukkaWasuu = this.selectedMeisai.shukkaWasuu ? this.selectedMeisai.shukkaWasuu : undefined;
    // 実施日の初期値は予定日をセット。実施済の場合、後でイベントの実施日に変更される。
    this.inputForm.jisshiDate = this.selectedMeisai.yoteiDate;
  }

  /**
   *
   */
  async ngOnInit(): Promise<void> {
    this.eventService.setInputForm2(this.inputForm, this.selectedMeisai);
    this.eventService.updateJisshiNichirei2(this.inputForm, this.edukeDate);

    // 飼養ロットへ出荷の場合
    // 表示：出荷先ロット名 改行 出荷先農場+鶏舎名
    if (this.selectedMeisai.toShiyoulot && this.selectedMeisai.himodukiId) {
      const himodukiNyuukaEvent = (await this.eventService.genCustomerRootCollectionRef(COLLECTION_NAME.EVENT).doc(this.selectedMeisai.himodukiId).get()).data() as NyuukaEvent;
      if (himodukiNyuukaEvent) {
        this.shukkaSakiLotInfo = himodukiNyuukaEvent.taishouLotName
          + '\n' + this.commonService.getName(this.masterService.keishaMaster, himodukiNyuukaEvent.taishouKeisha, 'farmKeisha');
      }
    } else if (!this.selectedMeisai.toShiyoulot && this.selectedMeisai.himodukiId) {
      // 注文の場合
      // 表示：注文番号 改行 出荷先名
      const chumon = (await this.eventService.genCustomerRootCollectionRef(COLLECTION_NAME.CHUMON).doc(this.selectedMeisai.himodukiId).get()).data() as ChumonForIwamura;
      this.chumonInfo = this.selectedMeisai.himodukiId;
      if (chumon) {
        this.chumonInfo += '\n' + this.commonService.getName(this.masterService.shukkaSakiMaster, chumon.shukkaSakiId);
      }
    }
  }

  /**
   *
   */
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  /**
   * 出荷イベント登録
   */
  async register() {
    if (this.isProcessing) { return; }
    // 事前の羽数検査(非同期)も並走させないため、入口でフラグを立てる。
    // 検査の失敗を含むあらゆる中断経路でフラグが残らないよう、解除はfinallyで一括して行う。
    this.isProcessing = true;
    try {
      // 出荷羽数が現在羽数を超えている場合、メッセージが入る。
      // 問題ない場合は空文字
      const alertMsg = await this.getShukkaWasuuExceedGenzaiWasuuMsg();
      if (alertMsg) {
        alert(alertMsg);
        return;
      }

      // 紐付きIdがない場合confirm
      if (!this.selectedMeisai.himodukiId) {
        if (!confirm('出荷先が未登録です。このまま出荷しますか？')) { return; }
      }
      // 保存用に明細をコピー。はじかれた場合、元の明細を使用するため。
      const saveMeisai = cloneDeep(this.selectedMeisai);
      this.eventService.inputForm2Event2(this.inputForm, saveMeisai);
      if (saveMeisai.jisshiDate) {
        saveMeisai.shukkaWasuu = this.shukkaWasuu;
        const reqDto: ShukkaJisshiReqDto
          = {type: 'shukka', event: saveMeisai as JisshiDateNonNullable<ShukkaEvent>, isUndo: false};
        await this.eventService.updateNyuushukkaEvent2(reqDto).then(() => {
          // 成功した場合のみ明細にコピーする。
          this.selectedMeisai = saveMeisai;
          // 成功した場合のみ true を返す
          this.commonService.onClick(this.matDialogRef, true);
        }).catch(() => undefined);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * 出荷イベント取り消し
   */
  undo() {
    if (this.isProcessing) { return; }
    if (confirm('出荷を取り消しますか?')) {
      if (this.selectedMeisai.jisshiDate) {
        this.isProcessing = true;
        const reqDto: ShukkaJisshiReqDto
          = {type: 'shukka', event: this.selectedMeisai as JisshiDateNonNullable<ShukkaEvent>, isUndo: true};
        this.eventService.updateNyuushukkaEvent2(reqDto).then(() => {
          // 開発者向け通知(取り消しが成功した場合のみ送信する。メール失敗は取り消し失敗として扱わない)
          const meilText = this.mailService.genMailTextForEventUndo(this.selectedMeisai);
          this.mailService.sendMailToDeveloper(meilText);
          // 成功した場合のみ true を返す
          this.commonService.onClick(this.matDialogRef, true);
        }).catch(() => undefined)
          .finally(() => this.isProcessing = false);
      }
    }
  }

  /**
   * 出荷イベント削除
   */
  delete() {
    if (confirm('作業を削除しますか?')) {
      this.eventService.deleteEvents([this.selectedMeisai]).then(() => {
        // 成功した場合のみ true を返す
        this.commonService.onClick(this.matDialogRef, true);
      }).catch();
    }
  }

  /**
   * 出荷羽数が現在羽数を超えている場合、
   * イベントにロット登録がされていない場合、
   * エラーメッサージを返す。
   * 問題ない場合は空文字を返す。
   */
  async getShukkaWasuuExceedGenzaiWasuuMsg() {
    const shiyoulotKey = this.selectedMeisai.eventTaishouIds.find(val => {
      return val.endsWith(SHIYOULOT_KEY_SUFFIX);
    });
    if (!shiyoulotKey) {
      return 'イベントに飼養ロットが登録されていません。';
    }
    const shiyoulotId = shiyoulotKey.replace(SHIYOULOT_KEY_SUFFIX, '');
    const shiyoulot = (await this.shiyoulotService.getShiyoulot(shiyoulotId)).data() as ShiyoulotV2;
    if (typeof this.shukkaWasuu !== 'undefined' && this.shukkaWasuu > this.commonService.calcGenzaiWasuuForShiyoulotFile(shiyoulot)) {
      return '現在羽数より多い羽数は出荷できません。';
    }
    return '';
  }
}
